import { useApp } from '@/contexts';

import { useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import {
  useGetIntegrationTypesQuery,
  useGetProviderMetadataMutation,
} from '@/features/environments/api/environmentsApi';
import { normalizeCredentialType } from '@/features/environments/utils/credentialTypeMapping';
import type { Credential, IntegrationProvider } from '@/types';

interface MetadataObject {
  name?: string;
  type?: string;
  label?: string;
  properties?: Array<{ name?: string; type?: string; label?: string }>;
}

function normalizeMetadata(metadata: unknown): MetadataObject[] {
  if (!metadata) return [];
  if (Array.isArray(metadata)) return metadata as MetadataObject[];
  if (typeof metadata === 'object') {
    const obj = metadata as Record<string, unknown>;
    if (Array.isArray(obj.resources)) return obj.resources as MetadataObject[];
    if (Array.isArray(obj.objects)) return obj.objects as MetadataObject[];
    if (Array.isArray(obj.Objects)) return obj.Objects as MetadataObject[];
    if (Array.isArray(obj.metadata)) return obj.metadata as MetadataObject[];
  }
  return [];
}

interface MetadataExplorerProps {
  tenantId: string;
  envName: string;
  credentials: Credential[];
}

/**
 * Metadata Explorer - browse connector metadata (objects, fields)
 * Matches dataProtect metaExplore.js - uses getProviderMetadata to fetch metadata for selected connector
 */
export const MetadataExplorer = ({ tenantId, envName, credentials }: MetadataExplorerProps) => {
  const { appId } = useApp();
  const [selectedCred, setSelectedCred] = useState<Credential | null>(null);
  const [selectedObject, setSelectedObject] = useState<MetadataObject | null>(null);
  const [selectedField, setSelectedField] = useState<{
    name?: string;
    type?: string;
    label?: string;
  } | null>(null);
  const [objectSearch, setObjectSearch] = useState('');
  const [fieldSearch, setFieldSearch] = useState('');

  const { data: providers = [] } = useGetIntegrationTypesQuery();
  const [getMetadata, { data: metadata, isLoading: checking }] = useGetProviderMetadataMutation();

  const connectorCreds = credentials.filter(
    (c) =>
      c.credentialType !== 'AdapterSecret' &&
      c.credentialType !== 'EnvironmentVariables' &&
      c.credentialType !== 'EnvVars'
  );
  const sortedCreds = [...connectorCreds].sort((a, b) =>
    (a.providerLabel || '').localeCompare(b.providerLabel || '')
  );

  const findProvider = (cred: Credential): IntegrationProvider | undefined =>
    providers.find((p) => p.type === cred.providerLabel) ||
    providers.find((p) => p.type === normalizeCredentialType(cred.credentialType));

  const handleSelectConnector = async (cred: Credential) => {
    setSelectedCred(cred);
    setSelectedObject(null);
    setSelectedField(null);
    setObjectSearch('');
    setFieldSearch('');

    const provider = findProvider(cred);
    if (!provider?.metadataEndpoint) return;

    try {
      await getMetadata({
        tenantId,
        appId,
        metadataEndpoint: provider.metadataEndpoint,
        metadataApi: provider.metadataApi || 'TRANS_API_URL',
        credentialType: provider.credentialType,
        credentialsJson: cred.credentialsJson || {},
        providerType: provider.type,
        envName, // Always pass envName (needed for adapters and OAuth)
        connectionKey: provider.credentials?.includes('Oauth') ? cred.key : undefined,
      }).unwrap();
    } catch {
      // Error shown via metadata being null
    }
  };

  const objects = normalizeMetadata(metadata);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 'bold', mb: 2, px: 0.75 }}>
        Metadata Explorer
      </Typography>
      <Box
        sx={{
          p: 2,
          outline: '1px solid rgb(205, 211, 224)',
          boxShadow: '0px -2px 6px 0px #999999',
          backgroundColor: '#fafafa',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Connectors
            </Typography>
            <Select
              size="small"
              value={String(selectedCred?.key ?? '')}
              onChange={(e) => {
                const cred = sortedCreds.find((c) => c.key === e.target.value);
                if (cred) handleSelectConnector(cred);
              }}
              displayEmpty
              sx={{ minWidth: 140 }}
              disabled={sortedCreds.length === 0}
              renderValue={() =>
                selectedCred
                  ? `${
                      findProvider(selectedCred)?.label ||
                      selectedCred.providerLabel ||
                      selectedCred.key
                    } (${selectedCred.key})`
                  : 'Connectors'
              }
            >
              <MenuItem value="">
                <em>Select connector</em>
              </MenuItem>
              {sortedCreds.map((c) => {
                const p = findProvider(c);
                const name = p?.label || c.providerLabel || c.key;
                return (
                  <MenuItem key={c.key} value={c.key}>
                    {name} ({c.key})
                  </MenuItem>
                );
              })}
            </Select>
            {checking ? (
              <Typography variant="caption" color="text.secondary">
                Loading...
              </Typography>
            ) : metadata ? (
              <Typography variant="caption" sx={{ color: '#019644' }}>
                ✓ {objects.length} Objects
              </Typography>
            ) : selectedCred ? (
              <Typography variant="caption" color="error">
                Failed to load
              </Typography>
            ) : null}
          </Box>

          {selectedCred && objects.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Objects
                </Typography>
                <Select
                  size="small"
                  value={String(selectedObject?.name ?? selectedObject?.type ?? '')}
                  onChange={(e) => {
                    const obj = objects.find(
                      (o) => o.name === e.target.value || o.type === e.target.value
                    );
                    setSelectedObject(obj || null);
                    setSelectedField(null);
                    setFieldSearch('');
                  }}
                  onClose={() => setObjectSearch('')}
                  displayEmpty
                  sx={{ minWidth: 140 }}
                  MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 350 } } }}
                  renderValue={() => (selectedObject?.name || selectedObject?.type) ?? 'Objects'}
                >
                  <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                      {selectedCred
                        ? findProvider(selectedCred)?.label || selectedCred.providerLabel
                        : ''}{' '}
                      Objects
                    </Typography>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Filter..."
                      fullWidth
                      value={objectSearch}
                      onChange={(e) => setObjectSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </ListSubheader>
                  {objects
                    .filter((o) => {
                      if (!objectSearch) return true;
                      const term = objectSearch.toLowerCase();
                      return (
                        (o.name ?? '').toLowerCase().includes(term) ||
                        (o.type ?? '').toLowerCase().includes(term) ||
                        (o.label ?? '').toLowerCase().includes(term)
                      );
                    })
                    .map((o, i) => (
                      <MenuItem key={i} value={String(o.name ?? o.type ?? '')}>
                        {String(o.name ?? o.type ?? o.label ?? 'Unknown')}
                      </MenuItem>
                    ))}
                </Select>
              </Box>
              {selectedObject?.properties && selectedObject.properties.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Fields
                  </Typography>
                  <Select
                    size="small"
                    value={String(selectedField?.name ?? selectedField?.label ?? '')}
                    onChange={(e) => {
                      const f = selectedObject.properties?.find(
                        (p) => p.name === e.target.value || p.label === e.target.value
                      );
                      setSelectedField(f || null);
                    }}
                    onClose={() => setFieldSearch('')}
                    displayEmpty
                    sx={{ minWidth: 140 }}
                    MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 350 } } }}
                    renderValue={() => selectedField?.label ?? selectedField?.name ?? 'Fields'}
                  >
                    <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                        Fields
                      </Typography>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Filter..."
                        fullWidth
                        value={fieldSearch}
                        onChange={(e) => setFieldSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </ListSubheader>
                    {selectedObject.properties
                      .filter((p) => {
                        if (!fieldSearch) return true;
                        const term = fieldSearch.toLowerCase();
                        return (
                          (p.name ?? '').toLowerCase().includes(term) ||
                          (p.type ?? '').toLowerCase().includes(term) ||
                          (p.label ?? '').toLowerCase().includes(term)
                        );
                      })
                      .map((p, i) => (
                        <MenuItem key={i} value={String(p.name ?? p.label ?? '')}>
                          {String(p.label ?? p.name ?? 'Unknown')}
                        </MenuItem>
                      ))}
                  </Select>
                </Box>
              )}
            </Box>
          )}

          {(selectedObject || selectedField) && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: 'white',
                borderRadius: 1,
                boxShadow: '0px 0px 10px rgba(99,99,99,0.25)',
              }}
            >
              {selectedObject && (
                <Typography variant="subtitle2" sx={{ textDecoration: 'underline', mb: 1 }}>
                  {selectedObject.name ?? selectedObject.type} Object
                </Typography>
              )}
              {selectedField && (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  System Name: {selectedField.name ?? selectedField.label}
                  {selectedField.type && ` | Type: ${selectedField.type}`}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
